import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { useState } from 'react';

import { MoleculeDiagram } from './IonsBondingDiagrams';
import {
  COVALENT_MOLECULE_CASES,
  ELEMENT_FACTS,
  HIGH_MELTING_THRESHOLD_C,
  SHARED_PAIR_CHOICES,
  STRUCTURE_LABELS,
  STRUCTURE_ORDER,
  STRUCTURE_SUBSTANCES,
  checkPropertyPrediction,
  checkSharedPairs,
  checkStructureChoice,
  formatMeltingPoint,
  outerShellTarget,
  type MeltingBand,
  type PropertyFieldResult,
  type PropertyPrediction,
  type SharedPairResult,
  type StructureChoiceResult,
  type StructureSubstance,
  type StructureType,
} from './ionsBonding';
import { Feedback, Panel } from './IonsBondingShared';
import { choiceClass, pillClass, primaryButtonClass } from './IonsBondingStyles';

function SharedPairs() {
  const [moleculeId, setMoleculeId] = useState(COVALENT_MOLECULE_CASES[0].id);
  const [choice, setChoice] = useState<number | null>(null);
  useScienceTutorSection('SharedPairs', { moleculeId, choice });
  const [result, setResult] = useState<SharedPairResult | null>(null);
  const molecule = COVALENT_MOLECULE_CASES.find((entry) => entry.id === moleculeId) ?? COVALENT_MOLECULE_CASES[0];
  const markers = [...new Map(molecule.structure.atoms.map((item) => [item.element, item.marker])).entries()];

  return (
    <Panel
      eyebrow="Week 4 · covalent bonds"
      title="Share electrons to fill shells"
      description="Predict how many electron pairs the atoms share, then reveal the dot-and-cross diagram."
    >
      <div className="flex flex-wrap gap-2" role="group" aria-label="Choose a molecule">
        {COVALENT_MOLECULE_CASES.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-pressed={entry.id === moleculeId}
            aria-label={entry.name}
            onClick={() => {
              setMoleculeId(entry.id);
              setChoice(null);
              setResult(null);
            }}
            className={pillClass(entry.id === moleculeId)}
          >
            {entry.formula}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          <p className="text-sm font-extrabold text-ink">
            {molecule.name}, {molecule.formula}
          </p>
          <ul className="mt-2 grid gap-1 text-sm font-medium text-slate2">
            {[...new Set(molecule.structure.atoms.map((item) => item.element))].map((symbol) => (
              <li key={symbol}>
                {ELEMENT_FACTS[symbol].name}: {ELEMENT_FACTS[symbol].valenceElectrons} outer electron
                {ELEMENT_FACTS[symbol].valenceElectrons === 1 ? '' : 's'}, needs {outerShellTarget(symbol)} for a full shell
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm font-extrabold text-ink">How many electron pairs are shared in total?</p>
          <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Shared pairs">
            {SHARED_PAIR_CHOICES.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={choice === option}
                aria-label={`${option} shared pair${option === 1 ? '' : 's'}`}
                onClick={() => {
                  setChoice(option);
                  setResult(null);
                }}
                className={pillClass(choice === option)}
              >
                {option}
              </button>
            ))}
          </div>
          <button
            type="button"
            disabled={choice === null}
            onClick={() => choice !== null && setResult(checkSharedPairs(molecule.id, choice))}
            className={`${primaryButtonClass} mt-4`}
          >
            Reveal diagram
          </button>
          {result ? (
            <Feedback correct={result.correct} label="Shared pair feedback">
              <p>{result.feedback}</p>
            </Feedback>
          ) : null}
        </div>
        <div className="min-w-0 rounded-[1.5rem] border border-brand-sky/25 bg-gradient-to-br from-white to-wash-sky p-4">
          {result ? (
            <>
              <MoleculeDiagram
                structure={molecule.structure}
                variant="dot-cross"
                label={`Dot-and-cross diagram of ${molecule.name}: ${result.expected} shared pair${result.expected === 1 ? '' : 's'}`}
              />
              <p className="mt-2 text-center text-xs font-bold leading-5 text-slate2">
                {markers
                  .map(([symbol, marker]) => `${marker === 'dot' ? '● dots' : '× crosses'}: ${ELEMENT_FACTS[symbol].name}`)
                  .join(' · ')}
                . Shared pairs sit where the shells overlap.
              </p>
            </>
          ) : (
            <p className="flex min-h-48 items-center justify-center text-center text-sm font-bold text-slate2">
              The dot-and-cross diagram appears after your prediction.
            </p>
          )}
        </div>
      </div>
    </Panel>
  );
}

function PropertyCard({ substance, hideName }: { substance: StructureSubstance; hideName: boolean }) {
  const rows = [
    ['Melting point', formatMeltingPoint(substance)],
    ['Conducts as a solid', substance.conductsSolid ? 'Yes' : 'No'],
    [
      'Conducts when molten',
      substance.conductsMolten === null ? 'Does not melt at 100 kPa' : substance.conductsMolten ? 'Yes' : 'No',
    ],
    [
      'In water',
      substance.aqueous === 'conducts'
        ? 'Dissolves; solution conducts'
        : substance.aqueous === 'does-not-conduct'
          ? 'Solution does not conduct'
          : substance.aqueous === 'reacts'
            ? 'Reacts with water'
            : 'Does not dissolve',
    ],
    ['Solubility', substance.solubility],
  ];
  return (
    <dl className="grid gap-2 rounded-2xl border border-hairline bg-white p-4 text-sm" aria-label="Property evidence">
      {hideName ? null : (
        <div className="flex justify-between gap-3">
          <dt className="font-bold text-slate2">Substance</dt>
          <dd className="text-right font-extrabold text-ink">
            {substance.name} ({substance.formula})
          </dd>
        </div>
      )}
      {rows.map(([term, value]) => (
        <div key={term} className="flex justify-between gap-3">
          <dt className="font-bold text-slate2">{term}</dt>
          <dd className="text-right font-extrabold text-ink">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

const MELTING_OPTIONS: { value: MeltingBand; label: string }[] = [
  { value: 'high', label: `High (${HIGH_MELTING_THRESHOLD_C} °C or more)` },
  { value: 'low', label: `Low (below ${HIGH_MELTING_THRESHOLD_C} °C)` },
];

const FIELD_LABELS: Record<keyof PropertyPrediction, string> = {
  meltingBand: 'Melting point',
  conductsSolid: 'Conducts as a solid',
  conductsMolten: 'Conducts when molten',
};

function YesNo({ label, value, onChange }: { label: string; value: boolean | null; onChange: (value: boolean) => void }) {
  return (
    <fieldset>
      <legend className="text-sm font-extrabold text-ink">{label}</legend>
      <div className="mt-2 flex gap-2">
        {[true, false].map((option) => (
          <button
            key={String(option)}
            type="button"
            aria-pressed={value === option}
            aria-label={`${label}: ${option ? 'yes' : 'no'}`}
            onClick={() => onChange(option)}
            className={pillClass(value === option)}
          >
            {option ? 'Yes' : 'No'}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function StructureProperties() {
  const [task, setTask] = useState<'identify' | 'predict'>('identify');
  const [substanceId, setSubstanceId] = useState(STRUCTURE_SUBSTANCES[0].id);
  const [structureChoice, setStructureChoice] = useState<StructureType | null>(null);
  const [structureResult, setStructureResult] = useState<StructureChoiceResult | null>(null);
  const [melting, setMelting] = useState<MeltingBand | null>(null);
  const [solid, setSolid] = useState<boolean | null>(null);
  const [molten, setMolten] = useState<boolean | null>(null);
  useScienceTutorSection('StructureProperties', { task: tutorSelection(task), substanceId, structureChoice, melting, solid, molten });
  const [propertyResult, setPropertyResult] = useState<PropertyFieldResult[] | null>(null);
  const substanceIndex = STRUCTURE_SUBSTANCES.findIndex((entry) => entry.id === substanceId);
  const substance = STRUCTURE_SUBSTANCES[Math.max(substanceIndex, 0)];

  const clear = () => {
    setStructureChoice(null);
    setStructureResult(null);
    setMelting(null);
    setSolid(null);
    setMolten(null);
    setPropertyResult(null);
  };

  return (
    <Panel
      eyebrow="Week 4 · structure and properties"
      title="Four structure types"
      description="Ionic lattice, simple molecular, covalent network or metallic: use evidence to identify the structure, or use the structure to predict the evidence."
    >
      <div className="grid max-w-md grid-cols-2 rounded-full bg-surface p-1" role="group" aria-label="Choose task">
        {(['identify', 'predict'] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={task === option}
            onClick={() => {
              setTask(option);
              clear();
            }}
            className={`rounded-full px-3 py-2 text-sm font-extrabold ${task === option ? 'bg-ink text-white' : 'text-slate2'}`}
          >
            {option === 'identify' ? 'Identify the structure' : 'Predict the properties'}
          </button>
        ))}
      </div>

      <label className="mt-5 block text-sm font-extrabold text-ink">
        {task === 'identify' ? 'Mystery substance' : 'Substance'}
        <select
          value={substance.id}
          onChange={(event) => {
            setSubstanceId(event.target.value);
            clear();
          }}
          className="mt-2 block w-full rounded-2xl border border-hairline bg-white px-4 py-3 text-sm font-bold text-ink sm:max-w-sm"
        >
          {STRUCTURE_SUBSTANCES.map((entry, index) => (
            <option key={entry.id} value={entry.id}>
              {task === 'identify' ? `Substance ${index + 1}` : `${entry.name} (${STRUCTURE_LABELS[entry.structure].toLowerCase()})`}
            </option>
          ))}
        </select>
      </label>

      {task === 'identify' ? (
        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <PropertyCard substance={substance} hideName={!structureResult} />
          <div className="min-w-0">
            <p className="text-sm font-extrabold text-ink">Which structure fits this evidence?</p>
            <div className="mt-2 grid gap-2 sm:grid-cols-2" role="group" aria-label="Structure type">
              {STRUCTURE_ORDER.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={structureChoice === option}
                  onClick={() => {
                    setStructureChoice(option);
                    setStructureResult(null);
                  }}
                  className={choiceClass(structureChoice === option)}
                >
                  {STRUCTURE_LABELS[option]}
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={!structureChoice}
              onClick={() => structureChoice && setStructureResult(checkStructureChoice(substance, structureChoice))}
              className={`${primaryButtonClass} mt-4`}
            >
              Check structure
            </button>
            {structureResult ? (
              <Feedback correct={structureResult.correct} label="Structure feedback">
                <p>{structureResult.feedback}</p>
              </Feedback>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <div className="grid gap-4">
            <fieldset>
              <legend className="text-sm font-extrabold text-ink">Melting point</legend>
              <div className="mt-2 flex flex-wrap gap-2">
                {MELTING_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={melting === option.value}
                    onClick={() => setMelting(option.value)}
                    className={pillClass(melting === option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </fieldset>
            <YesNo label="Conducts as a solid" value={solid} onChange={setSolid} />
            <YesNo label="Conducts when molten" value={molten} onChange={setMolten} />
            <button
              type="button"
              disabled={melting === null || solid === null || molten === null}
              onClick={() =>
                melting !== null &&
                solid !== null &&
                molten !== null &&
                setPropertyResult(
                  checkPropertyPrediction(substance, { meltingBand: melting, conductsSolid: solid, conductsMolten: molten }),
                )
              }
              className={`${primaryButtonClass} justify-self-start`}
            >
              Check properties
            </button>
          </div>
          <div className="min-w-0">
            {propertyResult ? (
              <>
                <Feedback
                  correct={propertyResult.every((item) => item.correct)}
                  label="Property feedback"
                  title={`${propertyResult.filter((item) => item.correct).length} of ${propertyResult.length} properties match`}
                >
                  <ul className="space-y-1">
                    {propertyResult.map((item) => (
                      <li key={item.field}>
                        <strong className="text-ink">
                          {item.correct ? '✓' : '✗'} {FIELD_LABELS[item.field]}:
                        </strong>{' '}
                        {item.feedback}
                      </li>
                    ))}
                  </ul>
                </Feedback>
                <div className="mt-4">
                  <PropertyCard substance={substance} hideName={false} />
                </div>
              </>
            ) : (
              <p className="rounded-2xl border border-dashed border-hairline bg-surface p-4 text-sm font-bold text-slate2">
                The measured properties appear after you check.
              </p>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
}

export function BondingMode() {
  return (
    <div className="space-y-6">
      <SharedPairs />
      <StructureProperties />
    </div>
  );
}
